/* eslint-disable no-unused-expressions, quotes */
import type { ReactNode, RefObject } from "react"
import { Fragment, createElement } from "react"

import { AxiosError, HttpStatusCode } from "axios"
import entries from "lodash-es/entries"
import eq from "lodash-es/eq"
import flatMap from "lodash-es/flatMap"
import forEach from "lodash-es/forEach"
import includes from "lodash-es/includes"
import isArray from "lodash-es/isArray"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import isString from "lodash-es/isString"
import join from "lodash-es/join"
import keys from "lodash-es/keys"
import map from "lodash-es/map"
import minBy from "lodash-es/minBy"
import orderBy from "lodash-es/orderBy"
import range from "lodash-es/range"
import sortBy from "lodash-es/sortBy"
import uniqBy from "lodash-es/uniqBy"
import values from "lodash-es/values"
import { DateTime } from "luxon"
import memoizeOne from "memoize-one"

import type { TTFunction } from "$/i18n/i18n"

// Deep, not the "@/3514/components" barrel. This module is reached from coaching-chat.slice, which
// the store imports directly; going through the components barrel from here pulls the entire
// component tree into store initialisation and the barrel gets re-entered before its own members are
// assigned (`TypeError: Cannot read properties of undefined (reading 'Tiny')` out of typography).
// Both modules below are leaves: special-char.types has no imports at all, special-char.config only
// reaches react and its sibling types.
import { SpecialChars } from "@/3514/components/shared/typography/special-char/special-char.config"
import { ESpecialChar } from "@/3514/components/shared/typography/special-char/special-char.types"
import { THEME } from "@/3514/theme"
import { generateUuid, isUnprocessableEntityError, safeTransition } from "@/3514/utils"
import { seconds } from "@/3514/utils/time.utils"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"
import type { TEmptyCallback } from "@/shared/types/functions"

import { type TCoachingChatSeedMutationError as TSeedAnswerMutationError } from "../api"
import { coachingChatUiConfig as uiConfig } from "../config"
import {
    ECoachingChatConversationApiMode as EApiMode,
    ECoachingChatConversationErrorVariant as EConversationError,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatFormErrorVariant as EFormError,
    ECoachingChatMessageTransport as EMessageTransport,
    ECoachingChatMatrixFeedbackModalChartQuadrantKey as EQuadrantKey,
    ECoachingChatSummaryCardMode as ESummaryCardMode,
    type TCoachingChatSeedQuestionFormField as IChatFormField,
    type ICoachingChatMessageQueryModel as IConversationMessageQueryModel,
    type ICoachingChatConversationQueryModel as IConversationQueryModel,
    type ICoachingChatConversationSummaryCardQueryModel as IConversationQuerySummaryCard,
    type ICoachingChatNewMessagesQueryModel as INewMessagesQueryModel,
    type ICoachingChatConversationSummaryCardQueryDto as ISummaryCardQueryDto,
    type TCoachingChatSeedQuestion as TChatSeedQuestion,
    type TCoachingChatConversation as TConversation,
    type TCoachingChatConversationError as TConversationError,
    type TCoachingChatConversationMessage as TConversationMessage,
    type TCoachingChatConversationStatusDto as TConversationStatus,
    type TCoachingChatMatrixFeedbackModalChartDiscreteCoords as TDiscreteCoords,
    type TCoachingChatFormError as TFormError,
    type TCoachingChatMatrixFeedbackModalChartGridCell as TGridCell,
    type TCoachingChatMatrixFeedbackModalChartGridRect as TGridRect,
    type TCoachingChatSocketHistoryEventData as THistoryEventData,
    type TCoachingChatConversationMessage as TMessage,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"

import { coachingChatApiUtils as apiUtils } from "./coaching-chat.api.utils"

const storageService: StorageService = new StorageService(EStorageServiceType.Session)

const tabId = {
    createAndSave(): void {
        storageService.setItem(SessionStorageServiceKeys.TabId(), generateUuid())
    },
    get(): string {
        return storageService.getItem(SessionStorageServiceKeys.TabId())
    }
}

const insertionClassesUtils = {
    getChatTypingIndicatorClassNamesStringForInsertion(): string {
        return `     
          @keyframes chatTypingBounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); }}
          .animate-chatTypingBounce-dot { animation: chatTypingBounce 1.3s linear infinite; }
          .animate-delay-150 { animation-delay: 0.15s; }
          .animate-delay-300 { animation-delay: 0.3s; }
        `
    },
    getChatSummaryCardContentCssString(cssIdentifier: string, mode: ESummaryCardMode): string {
        return `
            .${cssIdentifier} { 
                text-align: initial; 
                text-wrap: pretty;
                font-size: ${THEME.FONT_SIZES[14]}px; 
                line-height: ${THEME.LINE_HEIGHTS[21]}px; 
                color: ${eq(mode, ESummaryCardMode.BuildInChat) ? THEME.COLORS.white : THEME.COLORS.gray[100]}; 
            } 
            .${cssIdentifier} span, 
            .${cssIdentifier} p, 
            .${cssIdentifier} strong, 
            .${cssIdentifier} li, 
            .${cssIdentifier}.b { 
                font-size: ${THEME.FONT_SIZES[14]}px; 
                line-height: ${THEME.LINE_HEIGHTS[21]}px; 
                color: ${eq(mode, ESummaryCardMode.BuildInChat) ? THEME.COLORS.white : THEME.COLORS.gray[100]}; }
            .${cssIdentifier} b, 
            .${cssIdentifier} strong { font-weight: ${THEME.FONT_WEIGHTS[800]}; }
            .${cssIdentifier} p:last-of-type { margin-bottom: 0; }
            .${cssIdentifier} ol { list-style-type: auto; list-style-position: inside; }
            .${cssIdentifier} ul { list-style-type: none; }
            .${cssIdentifier} ol, 
            .${cssIdentifier} ul { margin-left: 5px; margin-bottom: 0; }
            .${cssIdentifier} ol:not(:last-child),
            .${cssIdentifier} ul:not(:last-child), 
            .${cssIdentifier} p:not(:last-of-type) { margin-bottom: 15px; }
            .${cssIdentifier} ul > li { padding-left: 15px; position: relative; }
            .${cssIdentifier} ul > li::before { content: "•"; position: absolute; left: 0; font-size: ${THEME.FONT_SIZES[14]}px; }
        `
    },
    getChatMessageRichTextClasses(identifier: string): string {
        return `
            .${identifier} * { 
                color: ${THEME.COLORS.white};
                font-weight: ${THEME.FONT_WEIGHTS[300]};
                line-height: ${THEME.LINE_HEIGHTS[24]}px;
                font-size: ${THEME.FONT_SIZES[16]}px;
                text-wrap: pretty;
                hyphens: auto;
                white-space: pre-line;
            }
            .${identifier} strong { font-weight: ${THEME.FONT_WEIGHTS[800]}; }
            .${identifier} p, 
            .${identifier} ul { margin-bottom: 0px; }
            .${identifier} ul { line-height: ${THEME.LINE_HEIGHTS[24]}px; list-style-position: inside; list-style-type: disc; }
        `
    },
    getClassesForLegaliseRecord(cssIdentifier: string): string {
        return `
            .${cssIdentifier} * { 
                color: ${THEME.COLORS.gray[100]};
                font-weight: ${THEME.FONT_WEIGHTS[300]};
                line-height: ${THEME.LINE_HEIGHTS[18]}px;
                font-size: ${THEME.FONT_SIZES[12]}px;
                white-space: pre-line;
                text-wrap: pretty;
            }
            .${cssIdentifier} a, .${cssIdentifier} u { text-decoration: underline; text-underline-offset: 3px; }
            .${cssIdentifier} a { color: ${THEME.COLORS.accent}; }
            .${cssIdentifier} a:hover, .${cssIdentifier} a:focus { text-underline-offset: 5px; outline: none; }
            .${cssIdentifier} i, .${cssIdentifier} em { font-style: italic; }
            .${cssIdentifier} strong, .${cssIdentifier} b { font-weight: ${THEME.FONT_WEIGHTS[800]}; }
            .${cssIdentifier} p, .${cssIdentifier} ul, .${cssIdentifier} ol { margin-bottom: 0px; }
            .${cssIdentifier} ul, .${cssIdentifier} ol { list-style-position: outside; display: flex; flex-direction: column; row-gap: 5px; }
            .${cssIdentifier} ul { list-style-type: disc; }
            .${cssIdentifier} ol { list-style-type: decimal; }
        `
    }
}

const storeUtils = {
    mergeMessages(prev: TConversationMessage[], incoming: TConversationMessage[]): TConversationMessage[] {
        const messageMap = new Map<number, TConversationMessage>()

        forEach<TConversationMessage>(
            prev,
            (msg: TConversationMessage): Map<number, TConversationMessage> => msg?.id && messageMap.set(msg.id, msg)
        )

        forEach<TConversationMessage>(
            incoming,
            (msg: TConversationMessage): Map<number, TConversationMessage> => msg?.id && messageMap.set(msg.id, msg)
        )

        return sortBy<TConversationMessage>(Array.from(messageMap.values()), ["id"])
    },
    extractMessageIdsFromMessages(messages: TConversationMessage[]): string[] {
        if (isEmpty(messages)) return []

        return map<TConversationMessage, string>(messages, (m: TConversationMessage): string => m.clientMessageId)
    }
}

function getChatTransportFromConversationMode(mode: EApiMode): EMessageTransport {
    switch (mode) {
        case EApiMode.Sockets:
            return EMessageTransport.Sockets
        case EApiMode.Rest:
            return EMessageTransport.Rest
        case null:
        default:
            return null
    }
}

function _getChatStatusEnumFromConversationStatusEnum(status: TConversationStatus): EConversationStatus {
    switch (status) {
        case "completed":
            return EConversationStatus.Completed
        case "in_progress":
        default:
            return EConversationStatus.Ready
    }
}
const queriesModelsDataExtractors = {
    getConversationErrorVariantByAxiosError(
        error: AxiosError,
        options?: { isChatForceDisabled: boolean }
    ): EConversationError | undefined {
        if (
            includes<HttpStatusCode>(uiConfig.CHAT_LOCKING_HTTP_ERROR_STATUSES, error.status) ||
            options?.isChatForceDisabled
        )
            return EConversationError.ApiFailure

        if (includes<HttpStatusCode>(uiConfig.CHAT_NOT_LOCKING_HTTP_ERROR_STATUSES, error.status))
            return EConversationError.Api

        return undefined
    },
    adaptConversationToStore(queryModel: IConversationQueryModel): TConversation {
        return {
            id: queryModel.id,
            summary: queryModel.summaryCards,
            completedAt: queryModel.completedStamp,
            status: _getChatStatusEnumFromConversationStatusEnum(queryModel.status),
            isMatrixFeedbackCompleted: queryModel.isMatrixFeedbackCompleted,
            messages: map<IConversationMessageQueryModel, TConversationMessage>(
                queryModel.messages,
                (msg: IConversationMessageQueryModel): TConversationMessage => ({
                    id: msg.id,
                    transport: apiUtils.getChatMessageTransportEnumFromType(msg.transport),
                    conversationId: msg.conversationId,
                    content: msg.message,
                    author: msg.author,
                    clientMessageId: generateUuid(),
                    isStreamable: false,
                    status: EConversationMessagePhase.Done
                })
            )
        }
    },
    getNewMessagesErrors(model: INewMessagesQueryModel): TConversationError[] {
        return isEmpty(model.errors)
            ? []
            : ((): TConversationError[] => {
                  if (
                      isNumber(model.errors[0]?.conversationId) &&
                      !isNumber(model.errors[0]?.moderationId) &&
                      !isNumber(model.errors[0]?.messageId) &&
                      !isString(model.errors[0]?.message)
                  )
                      return [{ variant: EConversationError.AiStuck, payload: null }]

                  if (isNumber(model.errors[0].moderationId))
                      return [
                          {
                              variant: EConversationError.Violation,
                              payload: {
                                  messageId: model.errors[0].messageId,
                                  moderationId: model.errors[0].moderationId,
                                  message: model.errors[0].message
                              }
                          }
                      ]

                  if (isNumber(model.errors[0].conversationId) && !isNumber(model.errors[0].moderationId))
                      return [
                          {
                              variant: EConversationError.ApiFailure,
                              payload: null
                          }
                      ]

                  return [{ variant: EConversationError.Api, payload: { messageId: model.errors[0].messageId } }]
              })()
    },
    getNewMessagesSummaries(model: INewMessagesQueryModel): TSummaryCard[] {
        const summary: TSummaryCard[] = []

        forEach<IConversationMessageQueryModel>(model.messages, (m: IConversationMessageQueryModel): void => {
            if (!isEmpty(m.conversation.summaryCards))
                forEach<IConversationQuerySummaryCard>(
                    m.conversation.summaryCards,
                    (s: IConversationQuerySummaryCard): void => void summary.push(s)
                )
        })

        return uniqBy<TSummaryCard>(summary, "title")
    },
    getNewMessagesMessages(model: INewMessagesQueryModel): TConversationMessage[] {
        const messages: TConversationMessage[] = []

        forEach<IConversationMessageQueryModel>(
            model.messages,
            (m: IConversationMessageQueryModel): void =>
                void messages.push({
                    id: m.id,
                    transport: EMessageTransport.Rest,
                    conversationId: m.conversationId,
                    content: m.message,
                    author: m.author,
                    clientMessageId: generateUuid(),
                    isStreamable: false,
                    status: EConversationMessagePhase.Done
                })
        )

        return messages
    }
}

function adaptSummaryDtosToStoreModels(dtos: ISummaryCardQueryDto[], date?: string): TSummaryCard[] {
    return isEmpty(dtos)
        ? []
        : map<ISummaryCardQueryDto, TSummaryCard>(
              dtos,
              (card: ISummaryCardQueryDto): TSummaryCard => ({
                  title: keys(card)[0],
                  content: values(card)[0],
                  ...(!!date && { completedAt: date })
              })
          )
}

const formExtractors = {
    mapSeedQuestionsToFormFields(questions: TChatSeedQuestion[]): IChatFormField[] {
        return map<TChatSeedQuestion, IChatFormField>(
            questions,
            ({ question, questionUuid, answer }: TChatSeedQuestion, index: number): IChatFormField => ({
                index,
                id: questionUuid,
                answer: answer! ?? String(),
                question: question! ?? String()
            })
        )
    },
    getSeedAnswerMutationErrorMessage(
        isApiError: boolean,
        apiError: AxiosError,
        fieldId: string,
        t: TTFunction
    ): ReactNode {
        return createElement(
            Fragment,
            null,
            SpecialChars[ESpecialChar.Asterisk],
            isApiError
                ? eq(JSON.parse(apiError.config.data).question_uuid, fieldId)
                    ? isUnprocessableEntityError(apiError)
                        ? t("entities.coachingChat.chat.question.error.violation")
                        : t("entities.coachingChat.chat.question.error.save")
                    : null
                : null
        )
    },
    getFormErrorFromMutationError(err: TSeedAnswerMutationError): TFormError {
        if (isUnprocessableEntityError(err))
            return {
                variant: EFormError.ContentViolation,
                moderationId: err.response.data.error.moderation_id,
                message: err.response.data.error.msg
            }

        return { variant: EFormError.Api, moderationId: null, message: null }
    }
}

function getFormattedSummaryCardDate(date: string): string {
    return DateTime.fromISO(date).toFormat("LLL d")
}

function autoScrollToSection({
    status,
    chatRef,
    formRef,
    onScrollComplete,
    isReducedMotion
}: {
    status: EConversationStatus
    chatRef: RefObject<HTMLDivElement>
    formRef: RefObject<HTMLDivElement>
    onScrollComplete: TEmptyCallback
    isReducedMotion: boolean
}): void {
    includes<EConversationStatus>([EConversationStatus.Locked, EConversationStatus.ReadyToStart], status) &&
        (setTimeout(
            (): void =>
                safeTransition((): void =>
                    formRef.current?.scrollIntoView({
                        behavior: isReducedMotion ? "instant" : "smooth",
                        block: "start"
                    })
                ),
            seconds(0.5)
        ),
        onScrollComplete())
    includes<EConversationStatus>(
        [EConversationStatus.Ready, EConversationStatus.InProgress, EConversationStatus.Paused],
        status
    ) &&
        (setTimeout(
            (): void =>
                safeTransition((): void =>
                    chatRef.current?.scrollIntoView({
                        behavior: isReducedMotion ? "instant" : "smooth",
                        block: "start"
                    })
                ),
            seconds(0.5)
        ),
        onScrollComplete())
}

function getChatBannerSubtitleMessage({
    chatStatus,
    isSeedMutationLoading,
    isConversationMutationError,
    isFormFilling,
    t
}: {
    chatStatus: EConversationStatus
    isSeedMutationLoading: boolean
    isConversationMutationError: boolean
    isFormFilling: boolean
    t: TTFunction
}): string {
    const _isChatLocked: boolean = eq(chatStatus, EConversationStatus.Locked)
    const _isChatReadyToStart: boolean = eq(chatStatus, EConversationStatus.ReadyToStart)
    const _isChatPaused: boolean = eq(chatStatus, EConversationStatus.Paused)

    if (_isChatLocked) return t("entities.coachingChat.chat.banner.hintLocked")
    if (isFormFilling) return t("entities.coachingChat.chat.banner.hintLocked")
    if (isSeedMutationLoading) return t("entities.coachingChat.chat.banner.hintLocked")

    if (_isChatReadyToStart && !isConversationMutationError && !isFormFilling)
        return t("entities.coachingChat.chat.banner.hintReady")
    if ((_isChatReadyToStart || _isChatPaused) && isConversationMutationError && !isFormFilling)
        return t("entities.coachingChat.chat.banner.hintError")

    return String("")
}

const _STREAMING_MESSAGE_KEY: string = `"message"`
const streamingUtils = {
    adaptHistoryMessageToUiMessages(historyData: THistoryEventData[], conversationId: number): TConversationMessage[] {
        return map<THistoryEventData, TConversationMessage>(
            historyData,
            (m: THistoryEventData): TMessage => ({
                id: m.msg_id,
                transport: apiUtils.getChatMessageTransportEnumFromType(m.transport),
                author: apiUtils.getChatMessageAuthorEnumFromType(m.role),
                content: m.content,
                conversationId,
                clientMessageId: generateUuid(),
                isStreamable: false,
                status: EConversationMessagePhase.Done
            })
        )
    },
    extractMessageFromBuffer(buffer: string): string {
        if (!buffer) return ""

        const keyIndex: number = buffer.indexOf(_STREAMING_MESSAGE_KEY)
        if (keyIndex === -1) return ""

        const valueQuoteStart: number = buffer.indexOf(`"`, keyIndex + _STREAMING_MESSAGE_KEY.length)
        if (valueQuoteStart === -1) return ""

        let i: number = valueQuoteStart + 1
        let result: string = ""
        let escaped: boolean = false

        for (; i < buffer.length; i++) {
            const ch: string = buffer[i]

            if (escaped) {
                if (ch === "n") result += "\n"
                else if (ch === "t") result += "\t"
                else result += ch

                escaped = false
                continue
            }

            if (ch === "\\") {
                escaped = true
                continue
            }

            if (ch === `"`) {
                break
            }

            result += ch
        }

        return result
    },
    getSummariesFromChunkString(chunkString: string, availableSummaryCards: Record<string, string>): TSummaryCard[] {
        const summaries: TSummaryCard[] = []

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parsed: any = JSON.parse(chunkString)

        if (parsed?.chat_summary && parsed?.is_chat_complete) {
            forEach<[string, string | string[]]>(
                entries(JSON.parse(chunkString).chat_summary),
                ([key, value]: [string, string | string[]]): void => {
                    const existingKey: string = availableSummaryCards?.[key]

                    if (existingKey)
                        summaries.push({
                            title: existingKey,
                            content: isArray(value)
                                ? `<ul>${join(
                                      map<string, string>(value, (v: string): string => `<li>${v}</li>`),
                                      ""
                                  )}</ul>`
                                : isString(value)
                                  ? value
                                  : undefined,
                            completedAt: undefined
                        })
                }
            )
        }
        return summaries
    },
    createRoundedRectPath(width: number, height: number, radius: number, inset: number): string {
        const adjustedWidth: number = Math.max(width - inset * 2, 0)
        const adjustedHeight: number = Math.max(height - inset * 2, 0)
        if (!adjustedWidth || !adjustedHeight) return ""

        const effectiveRadius: number = Math.min(radius, adjustedWidth / 2, adjustedHeight / 2)
        const left: number = inset
        const top: number = inset
        const right: number = inset + adjustedWidth
        const bottom: number = inset + adjustedHeight

        return [
            `M${left + effectiveRadius},${bottom}`,
            `H${right - effectiveRadius}`,
            `A${effectiveRadius},${effectiveRadius} 0 0 0 ${right},${bottom - effectiveRadius}`,
            `V${top + effectiveRadius}`,
            `A${effectiveRadius},${effectiveRadius} 0 0 0 ${right - effectiveRadius},${top}`,
            `H${left + effectiveRadius}`,
            `A${effectiveRadius},${effectiveRadius} 0 0 0 ${left},${top + effectiveRadius}`,
            `V${bottom - effectiveRadius}`,
            `A${effectiveRadius},${effectiveRadius} 0 0 0 ${left + effectiveRadius},${bottom}`,
            "Z"
        ].join(" ")
    }
}

const matrixFeedbackUtils = {
    createGridRect: memoizeOne(function (params: {
        containerHeight: number
        containerWidth: number
        gridSize: number
    }): TGridRect {
        if (!params.containerWidth || !params.containerHeight) return null

        const left: number = Math.max(0, (params.containerWidth - params.gridSize) / 2)
        const top: number = Math.max(0, (params.containerHeight - params.gridSize) / 2)

        const centerX: number = left + params.gridSize / 2
        const centerY: number = top + params.gridSize / 2

        return { left, top, size: params.gridSize, centerX, centerY }
    }),
    // pre-calculation of 6x6 cells, immediately in the desired order (by globalRow/globalCol), without orderBy/flatMap/range
    createGridCells: memoizeOne(function (params: {
        gridRect: TGridRect
        quadrantSize: number
        cellSize: number
    }): TGridCell[] {
        if (!params.gridRect) return []

        const { left: gL, top: gT }: TGridRect = params.gridRect

        type TQDef = { key: EQuadrantKey; left: number; top: number; baseRow: number; baseCol: number }
        const quads: TQDef[] = [
            { key: EQuadrantKey.QUADRANT_2, left: gL, top: gT, baseRow: 0, baseCol: 0 },
            { key: EQuadrantKey.QUADRANT_1, left: gL + params.quadrantSize, top: gT, baseRow: 0, baseCol: 3 },
            { key: EQuadrantKey.QUADRANT_3, left: gL, top: gT + params.quadrantSize, baseRow: 3, baseCol: 0 },
            {
                key: EQuadrantKey.QUADRANT_4,
                left: gL + params.quadrantSize,
                top: gT + params.quadrantSize,
                baseRow: 3,
                baseCol: 3
            }
        ]

        return orderBy<TGridCell>(
            flatMap<TQDef, TGridCell>(quads, (q: TQDef): TGridCell[] =>
                flatMap<number, TGridCell>(range(3), (r: number): TGridCell[] =>
                    map<number, TGridCell>(range(3), (c: number): TGridCell => {
                        const left: number = q.left + c * params.cellSize
                        const top: number = q.top + r * params.cellSize

                        return {
                            id: `${q.key}-${r}-${c}`,
                            quadrantKey: q.key,
                            row: r,
                            col: c,
                            left,
                            top,
                            width: params.cellSize,
                            height: params.cellSize,
                            centerX: left + params.cellSize / 2,
                            centerY: top + params.cellSize / 2,
                            globalRow: q.baseRow + r,
                            globalCol: q.baseCol + c
                        } as TGridCell
                    })
                )
            ),
            ["globalRow", "globalCol"],
            ["asc", "asc"]
        )
    }),
    clampValue(v: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, v))
    },
    // find cell by coords (in container system)
    findCellByCoordinates(cells: TGridCell[], x: number, y: number): TGridCell | null {
        if (isEmpty(cells)) return null

        return (
            minBy<TGridCell>(cells, (c: TGridCell): number => {
                const dx: number = c.centerX - x
                const dy: number = c.centerY - y
                return dx * dx + dy * dy
            }) ?? null
        )
    },
    // find cell id, which middle is nearest to the chart' center
    findNearestCellIdToCenter(cells: TGridCell[], centerX: number, centerY: number): string | null {
        if (isEmpty(cells)) return null

        const nearest: TGridCell = minBy<TGridCell>(cells, (c: TGridCell): number => {
            const dx: number = c.centerX - centerX
            const dy: number = c.centerY - centerY
            return dx * dx + dy * dy
        })

        return nearest?.id ?? null
    },
    // map cell to discrete coords [-3,-2,-1,+1,+2,+3]
    mapCellToDiscreteCoords(cell: TGridCell): TDiscreteCoords {
        const axis: number[] = [-3, -2, -1, +1, +2, +3]
        const x: number = axis[cell.globalCol] // from left to right
        const y: number = axis[cell.globalRow] * -1 //  from top to bottom with inversion (for top to be +)

        return { x, y }
    },
    getQuadrantKeyAtPosition(
        x: number,
        y: number,
        gridRect: TGridRect,
        prev: EQuadrantKey | null
    ): EQuadrantKey | null {
        if (!gridRect) return null
        const { centerX, centerY } = gridRect

        const dx: number = x - centerX
        const dy: number = y - centerY

        // symmetrical dead zone around the axes, slightly larger than a pixel to cut off sub-pixels/retina
        const EPS = 3

        const nearX: boolean = Math.abs(dx) <= EPS
        const nearY: boolean = Math.abs(dy) <= EPS

        // if on any of the axes, we don't redefine the quadrant (leave the previous one/nothing)
        if (nearX || nearY) return prev ?? null

        const right: boolean = dx > 0
        const down: boolean = dy > 0

        if (right && !down) return EQuadrantKey.QUADRANT_1
        if (!right && !down) return EQuadrantKey.QUADRANT_2
        if (!right && down) return EQuadrantKey.QUADRANT_3

        return EQuadrantKey.QUADRANT_4
    }
}

const utils = {
    tabId,
    insertionClassesUtils,
    storeUtils,
    queriesModelsDataExtractors,
    getChatTransportFromConversationMode,
    adaptSummaryDtosToStoreModels,
    formExtractors,
    getFormattedSummaryCardDate,
    autoScrollToSection,
    getChatBannerSubtitleMessage,
    streamingUtils,
    matrixFeedbackUtils
}

export { utils as coachingChatUiUtils }
